import { useMemo, useState, useEffect } from 'react';
import { X, CheckSquare, Square, Minus, Plus, ArrowUp, ArrowDown, Loader } from 'lucide-react';
import { reportTemplatesApi, parseValues, type Template, type ApplySelection } from '../lib/reportTemplatesApi';
import { SECTION_FIELDS, eintragText } from './TemplateEditModal';

type Eintrag = unknown;

interface Props {
  template: Template;
  reportId: number;
  darkMode: boolean;
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}

function bulletKey(itemId: number, index: number): string {
  return `${itemId}:${index}`;
}

const setzeEintragText = (e: Eintrag, text: string): Eintrag => {
  if (typeof e === 'string' || e === null || typeof e !== 'object') return text;
  const o = e as Record<string, unknown>;
  if ('problem' in o) return { ...o, problem: text };
  if ('bezeichnung' in o) return { ...o, bezeichnung: text };
  return { ...o, text };
};

/**
 * Vorschau + Bullet-weise Auswahl + Bearbeitung beim Anwenden einer Vorlage auf einen Bericht.
 * Editierungen gelten nur für diesen Bericht, nicht für die Vorlage selbst.
 */
export default function ApplyTemplateDialog({ template, reportId, darkMode, open, onClose, onApplied }: Props) {
  const sections = useMemo(() => {
    const byKey: Record<string, Record<string, { id: number; values: unknown[] }>> = {};
    for (const item of template.items) {
      if (!byKey[item.sectionKey]) byKey[item.sectionKey] = {};
      byKey[item.sectionKey][item.fieldKey] = { id: item.id, values: parseValues(item) };
    }
    return SECTION_FIELDS
      .map((sec) => ({
        ...sec,
        fields: sec.fields
          .map((f) => ({ ...f, item: byKey[sec.sectionKey]?.[f.key] }))
          .filter((f) => f.item && f.item.values.length > 0),
      }))
      .filter((sec) => sec.fields.length > 0);
  }, [template]);

  const allKeys = useMemo(() => {
    const keys: string[] = [];
    for (const sec of sections) {
      for (const f of sec.fields) {
        f.item!.values.forEach((_, idx) => keys.push(bulletKey(f.item!.id, idx)));
      }
    }
    return keys;
  }, [sections]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(allKeys));
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingReplace, setConfirmingReplace] = useState(false);
  const [skippedFields, setSkippedFields] = useState<string[] | null>(null);
  const [aiInstructions, setAiInstructions] = useState('');

  // Editierte Bullets (nur für diesen Dialog, nicht die Vorlage)
  const [editedValues, setEditedValues] = useState<Record<number, Eintrag[]>>(() => {
    const map: Record<number, Eintrag[]> = {};
    for (const item of template.items) {
      map[item.id] = [...parseValues(item)];
    }
    return map;
  });

  // Wenn sich template ändert, editedValues + aiInstructions zurücksetzen
  useEffect(() => {
    const map: Record<number, Eintrag[]> = {};
    for (const item of template.items) {
      map[item.id] = [...parseValues(item)];
    }
    setEditedValues(map);
    setSelected(new Set(allKeys));
    setAiInstructions('');
  }, [template.id]);

  if (!open) return null;

  const updateBullet = (itemId: number, index: number, value: string) => {
    setEditedValues((prev) => {
      const arr = [...prev[itemId]];
      arr[index] = setzeEintragText(arr[index], value);
      return { ...prev, [itemId]: arr };
    });
  };

  const addBullet = (itemId: number) => {
    setEditedValues((prev) => ({
      ...prev,
      [itemId]: [...prev[itemId], ''],
    }));
  };

  const moveBullet = (itemId: number, index: number, dir: -1 | 1) => {
    setEditedValues((prev) => {
      const arr = [...prev[itemId]];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...prev, [itemId]: arr };
    });
  };

  const toggleBullet = (itemId: number, index: number) => {
    const key = bulletKey(itemId, index);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const fieldKeys = (itemId: number, count: number) => Array.from({ length: count }, (_, i) => bulletKey(itemId, i));
  const fieldState = (itemId: number, count: number): 'all' | 'none' | 'some' => {
    const keys = fieldKeys(itemId, count);
    const n = keys.filter((k) => selected.has(k)).length;
    if (n === 0) return 'none';
    if (n === keys.length) return 'all';
    return 'some';
  };
  const toggleField = (itemId: number, count: number) => {
    const keys = fieldKeys(itemId, count);
    const allSelected = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const selectedCount = selected.size;

  const buildSelection = (): ApplySelection[] => {
    const items: ApplySelection[] = [];
    for (const sec of sections) {
      for (const f of sec.fields) {
        const currentValues = editedValues[f.item!.id] ?? [];
        const indices = currentValues.map((_, i) => i).filter((i) => selected.has(bulletKey(f.item!.id, i)));
        if (indices.length > 0) items.push({ itemId: f.item!.id, indices });
      }
    }
    return items;
  };

  const affectedFieldLabels = sections.flatMap((sec) =>
    sec.fields
      .filter((f) => {
        const currentValues = editedValues[f.item!.id] ?? [];
        return fieldState(f.item!.id, currentValues.length) !== 'none';
      })
      .map((f) => `${sec.sectionLabel} → ${f.label}`),
  );

  const apply = async (strategy: 'merge' | 'replace') => {
    if (selectedCount === 0) return;
    setApplying(true);
    setError(null);
    try {
      const result = await reportTemplatesApi.apply(template.id, reportId, strategy, buildSelection(), aiInstructions || undefined);
      if (result?.skippedFields && result.skippedFields.length > 0) {
        // Vorlage enthaelt Felder, die der aktuelle Berichts-Editor nicht (mehr) kennt —
        // wurden angewendet, aber ohne diese. Nicht einfach schweigen, siehe Code-Review.
        setSkippedFields(result.skippedFields);
      } else {
        onApplied();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Anwenden fehlgeschlagen');
    } finally {
      setApplying(false);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col ${card}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Vorlage „{template.name}" anwenden</h3>
          <button onClick={onClose} className={muted} aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className={`text-sm mb-4 ${muted}`}>
          Wähle, welche Punkte übernommen werden sollen. „Ergänzen" fügt sie zu den vorhandenen Inhalten
          hinzu (gleiche Einträge werden automatisch übersprungen), „Ersetzen" überschreibt die betroffenen Felder vollständig.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">KI-Anweisungen (optional)</label>
          <textarea
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            placeholder="z.B. 'Ergänze 3 weitere Stärken' oder 'Recherchiere auf der Website des Unternehmens'"
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg text-sm resize-y ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
          />
          <p className={`text-xs mt-1 ${muted}`}>Die KI wird diese Anweisungen berücksichtigen und kann damit die Eingabefelder ergänzen oder überarbeiten.</p>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {sections.length === 0 && (
            <p className={`text-sm italic ${muted}`}>Diese Vorlage enthält keine Einträge.</p>
          )}
          {sections.map((sec) => (
            <div key={sec.sectionKey} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h4 className="font-semibold text-sm mb-2">{sec.sectionLabel}</h4>
              {sec.fields.map((f) => {
                const item = f.item!;
                const currentValues = editedValues[item.id] ?? [];
                const state = fieldState(item.id, currentValues.length);
                return (
                  <div key={f.key} className="mb-3 last:mb-0">
                    <button
                      type="button"
                      onClick={() => toggleField(item.id, currentValues.length)}
                      className={`flex items-center gap-1.5 text-xs font-medium mb-1 ${muted}`}
                    >
                      {state === 'all' ? <CheckSquare className="w-3.5 h-3.5" /> : state === 'some' ? <Minus className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                      {f.label}
                    </button>
                    <div className="ml-5 space-y-1">
                      {currentValues.length === 0 ? (
                        <p className={`text-xs italic ${muted}`}>Keine Einträge</p>
                      ) : (
                        currentValues.map((v, idx) => {
                          const checked = selected.has(bulletKey(item.id, idx));
                          return (
                            <div key={idx} className="flex gap-1 items-start pr-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleBullet(item.id, idx)}
                                className="mt-1"
                              />
                              <input
                                type="text"
                                value={eintragText(v)}
                                onChange={(e) => updateBullet(item.id, idx, e.target.value)}
                                className={`flex-1 px-2 py-1 border rounded text-sm ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                              />
                              <button
                                onClick={() => moveBullet(item.id, idx, -1)}
                                disabled={idx === 0}
                                className={`p-1 rounded text-slate-400 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} disabled:opacity-30`}
                                title="Nach oben"
                                aria-label="Nach oben verschieben"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => moveBullet(item.id, idx, 1)}
                                disabled={idx === currentValues.length - 1}
                                className={`p-1 rounded text-slate-400 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} disabled:opacity-30`}
                                title="Nach unten"
                                aria-label="Nach unten verschieben"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })
                      )}
                      <button
                        onClick={() => addBullet(item.id)}
                        className={`mt-1 flex items-center gap-1 text-xs ${muted} hover:text-blue-600`}
                      >
                        <Plus className="w-3 h-3" />
                        Eintrag hinzufügen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-3 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
        )}

        <div className="mt-4 pt-3 border-t flex items-center justify-between gap-3 flex-wrap">
          <p className={`text-xs ${muted}`}>
            {selectedCount} von {allKeys.length} Punkten ausgewählt
            {affectedFieldLabels.length > 0 && (
              <> — betroffen: {affectedFieldLabels.join(', ')}</>
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-3 py-1.5 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              Abbrechen
            </button>
            <button
              onClick={() => setConfirmingReplace(true)}
              disabled={applying || selectedCount === 0}
              title="Überschreibt die betroffenen Felder vollständig"
              className={`px-3 py-1.5 text-sm rounded-lg border disabled:opacity-40 flex items-center gap-1.5 ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              {applying && <Loader className="w-3.5 h-3.5 animate-spin" />}
              Ersetzen
            </button>
            <button
              onClick={() => apply('merge')}
              disabled={applying || selectedCount === 0}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 font-medium flex items-center gap-1.5"
            >
              {applying && <Loader className="w-3.5 h-3.5 animate-spin" />}
              {applying ? 'Wird angewendet…' : 'Ergänzen'}
            </button>
          </div>
        </div>
      </div>
    </div>
    {skippedFields && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
        <div className={`rounded-xl border shadow-xl p-6 max-w-sm w-full ${card}`}>
          <h3 className="text-lg font-semibold mb-2">Vorlage teilweise übernommen</h3>
          <p className={`text-sm mb-6 ${muted}`}>
            {skippedFields.length} Feld{skippedFields.length === 1 ? '' : 'er'} aus dieser Vorlage ({skippedFields.join(', ')})
            {' '}{skippedFields.length === 1 ? 'wird' : 'werden'} vom aktuellen Berichts-Editor nicht mehr unterstützt und
            {' '}{skippedFields.length === 1 ? 'wurde' : 'wurden'} übersprungen. Alle anderen Punkte wurden übernommen.
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSkippedFields(null);
                onApplied();
              }}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Verstanden
            </button>
          </div>
        </div>
      </div>
    )}
    {confirmingReplace && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
        <div className={`rounded-xl border shadow-xl p-6 max-w-sm w-full ${card}`}>
          <h3 className="text-lg font-semibold mb-2">Felder wirklich ersetzen?</h3>
          <p className={`text-sm mb-6 ${muted}`}>
            {affectedFieldLabels.length > 0 ? (
              <>Bestehende Inhalte in {affectedFieldLabels.join(', ')} werden vollständig überschrieben — auch manuell eingetragene Punkte, die nicht aus dieser Vorlage stammen. Das lässt sich nicht rückgängig machen.</>
            ) : (
              <>Die betroffenen Felder werden vollständig überschrieben. Das lässt sich nicht rückgängig machen.</>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmingReplace(false)}
              className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              Abbrechen
            </button>
            <button
              onClick={() => {
                setConfirmingReplace(false);
                void apply('replace');
              }}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Ja, ersetzen
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
